import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, Store, LogOut, User, Search, Menu, Shield } from "lucide-react";
import { useState } from "react";
import NotificationBell from "@/components/NotificationBell";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <div className="kente-stripe h-1.5 w-full" />
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FDFBF7]/90 border-b border-[#E5D9C8]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center gap-1.5 sm:gap-4 min-w-0">
          <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="logo-link">
            <img src="/logo.png" alt="AgriNuhse logo" className="w-10 h-10 object-contain shrink-0" data-testid="header-logo-img" />
            <div className="leading-tight min-w-0">
              <div className="font-extrabold text-[#1E4D3B] text-base sm:text-lg truncate">AgriNuhse</div>
              <div className="hidden sm:block text-[10px] tracking-widest uppercase text-[#8C6D53] -mt-0.5">Farm Marketplace</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-6 ml-6 text-sm font-medium text-[#1F2421]">
            <Link to="/marketplace?category=livestock" className="link-underline" data-testid="nav-livestock">Livestock</Link>
            <Link to="/marketplace?category=poultry" className="link-underline" data-testid="nav-poultry">Poultry</Link>
            <Link to="/marketplace?category=agriculture" className="link-underline" data-testid="nav-agriculture">Agriculture</Link>
            <Link to="/marketplace?category=fishing" className="link-underline" data-testid="nav-fishing">Fishing</Link>
          </nav>

          <div className="flex-1" />

          <button
            onClick={() => nav("/marketplace")}
            className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F3ECE2] hover:bg-[#E5D9C8] text-[#1F2421] text-sm shrink-0"
            data-testid="search-button"
          >
            <Search className="w-4 h-4" /> Search farms & produce
          </button>

          {user && user.is_admin && (
            <Link to="/admin" className="hidden md:inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-[#C85A32] hover:text-[#B04B26] shrink-0" data-testid="nav-admin-dashboard">
              <Shield className="w-4 h-4" /> Admin
            </Link>
          )}

          {user && user.role === "seller" && !user.is_admin && (
            <Link to="/seller" className="hidden md:inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold text-[#1E4D3B] hover:text-[#C85A32] shrink-0" data-testid="nav-seller-dashboard">
              <Store className="w-4 h-4" /> Seller Dashboard
            </Link>
          )}

          <div className="shrink-0"><NotificationBell /></div>

          {user && (
            <Link to="/marketplace" className="sm:hidden p-2 rounded-lg hover:bg-[#F3ECE2] shrink-0" data-testid="nav-search-mobile">
              <Search className="w-5 h-5 text-[#1F2421]" />
            </Link>
          )}

          <Link to="/cart" className="relative p-2 rounded-lg hover:bg-[#F3ECE2] shrink-0" data-testid="nav-cart">
            <ShoppingBag className="w-5 h-5 text-[#1F2421]" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#C85A32] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count-badge">{count}</span>
            )}
          </Link>

          {user ? (
            <div className="relative shrink-0">
              <button onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-[#F3ECE2]" data-testid="nav-user-menu">
                <User className="w-5 h-5 text-[#1F2421]" />
              </button>
              {open && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#E5D9C8] rounded-xl shadow-lg p-2 z-50">
                  <div className="px-3 py-2 text-sm">
                    <div className="font-semibold">{user.name}</div>
                    <div className="text-xs text-[#5C6660]">{user.email}</div>
                    <div className="text-xs text-[#C85A32] mt-1 uppercase tracking-wider font-bold">{user.role}</div>
                  </div>
                  <div className="h-px bg-[#E5D9C8] my-1" />
                  <Link to="/orders" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm rounded hover:bg-[#F3ECE2]" data-testid="nav-my-orders">My Orders</Link>
                  {user.is_admin && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm rounded hover:bg-[#F3ECE2]" data-testid="nav-menu-admin">Admin Dashboard</Link>
                  )}
                  {user.role === "seller" && !user.is_admin && (
                    <Link to="/seller" onClick={() => setOpen(false)} className="block px-3 py-2 text-sm rounded hover:bg-[#F3ECE2]" data-testid="nav-menu-seller">Seller Dashboard</Link>
                  )}
                  <button onClick={() => { setOpen(false); logout(); nav("/"); }} className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-[#F3ECE2] text-[#D93838]" data-testid="nav-logout">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <Link to="/login" className="px-2 sm:px-3 py-2 text-sm font-semibold text-[#1E4D3B] hover:text-[#C85A32]" data-testid="nav-login">Sign in</Link>
              <Link to="/register" className="px-3 sm:px-4 py-2 rounded-lg btn-primary text-xs sm:text-sm whitespace-nowrap" data-testid="nav-register">Get Started</Link>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-24 border-t border-[#E5D9C8] bg-[#F3ECE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="AgriNuhse logo" className="w-10 h-10 object-contain shrink-0" data-testid="footer-logo-img" />
              <div className="font-extrabold text-[#1E4D3B] text-lg">AgriNuhse</div>
            </div>
            <p className="text-sm text-[#5C6660] mt-2">Connecting Ghanaian farmers, fishermen and herders to buyers across all 16 regions.</p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53] mb-3">Categories</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/marketplace?category=livestock" className="hover:text-[#C85A32]">Livestock</Link></li>
              <li><Link to="/marketplace?category=poultry" className="hover:text-[#C85A32]">Poultry</Link></li>
              <li><Link to="/marketplace?category=agriculture" className="hover:text-[#C85A32]">Agriculture</Link></li>
              <li><Link to="/marketplace?category=fishing" className="hover:text-[#C85A32]">Fishing</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53] mb-3">For Farmers</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/register" className="hover:text-[#C85A32]">Become a seller</Link></li>
              <li><Link to="/seller" className="hover:text-[#C85A32]">Seller Dashboard</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53] mb-3">Payments accepted</div>
            <div className="flex gap-2 flex-wrap">
              <span className="px-3 py-1 rounded-full text-xs font-bold text-slate-900 bg-[#FFCC00]">MTN MoMo</span>
              <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-[#E20613]">Telecel Cash</span>
              <span className="px-3 py-1 rounded-full text-xs font-bold text-white bg-[#00539B]">AT Money</span>
            </div>
          </div>
        </div>
        <div className="border-t border-[#E5D9C8] py-4 text-center text-xs text-[#5C6660]">© {new Date().getFullYear()} AgriNuhse Marketplace. Made with soil, salt and sunshine.</div>
      </footer>
    </div>
  );
}
